// apps/office/src/components/office/InteriorProductForm.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Loader2, Save, Package, Home, Building2, ImagePlus, X } from 'lucide-react';
import pb from '../../lib/pocketbaseClient';
import { useAuth } from '../../contexts/AuthContext';

const CATEGORY_TYPES = [
  { value: 'residential', label: 'Residential', icon: Home, color: 'emerald' },
  { value: 'commercial', label: 'Commercial', icon: Building2, color: 'blue' },
  { value: 'both', label: 'Both', icon: Package, color: 'purple' },
];

const UNIT_OPTIONS = ['sq.ft', 'running ft', 'piece', 'set', 'nos', 'package'];

/* Auto-generate next item ID */
const generateItemId = (existing) => {
  const nums = (existing || [])
    .map((p) => {
      const m = (p.itemId || '').match(/IP(\d+)/);
      return m ? parseInt(m[1], 10) : 0;
    })
    .filter((n) => n > 0);
  const max = nums.length ? Math.max(...nums) : 5000;
  return `IP${Math.max(max + 1, 5001)}`;
};

const InteriorProductForm = ({ open, onOpenChange, existingProduct, onSuccess }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isEditing = !!existingProduct;

  const [itemId, setItemId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [groupId, setGroupId] = useState('');
  const [unit, setUnit] = useState('sq.ft');
  const [categoryType, setCategoryType] = useState('both');
  const [prices, setPrices] = useState({ standard: {}, luxury: {} });
  const [notes, setNotes] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  /* Fetch groups + subcategories */
  const { data: groups = [] } = useQuery({
    queryKey: ['interior-groups'],
    queryFn: () =>
      pb.collection('interior_product_groups').getFullList({
        sort: 'sortOrder,name',
        filter: 'isActive = true',
      }),
    enabled: open,
  });

  const { data: subcategories = [] } = useQuery({
    queryKey: ['interior-subcategories'],
    queryFn: () =>
      pb.collection('interior_subcategories').getFullList({
        sort: 'sortOrder,name',
        filter: 'isActive = true',
      }),
    enabled: open,
  });

  /* Fetch all products for itemId generation (create mode) */
  const { data: allProducts = [] } = useQuery({
    queryKey: ['interior-products-all-mini'],
    queryFn: () =>
      pb.collection('interior_products').getFullList({ fields: 'id,itemId' }).catch(() => []),
    enabled: open && !isEditing,
  });

  /* Populate form */
  useEffect(() => {
    if (!open) return;
    if (existingProduct) {
      setItemId(existingProduct.itemId || '');
      setName(existingProduct.name || '');
      setDescription(existingProduct.description || '');
      setGroupId(existingProduct.groupId || '');
      setUnit(existingProduct.unit || 'sq.ft');
      setCategoryType(existingProduct.categoryType || 'both');
      setPrices(existingProduct.prices || { standard: {}, luxury: {} });
      setNotes(existingProduct.notes || '');
      setImagePreview(existingProduct.image ? pb.files.getURL(existingProduct, existingProduct.image) : '');
      setImageFile(null);
    } else {
      setItemId(generateItemId(allProducts));
      setName('');
      setDescription('');
      setGroupId('');
      setUnit('sq.ft');
      setCategoryType('both');
      setPrices({ standard: {}, luxury: {} });
      setNotes('');
      setImageFile(null);
      setImagePreview('');
    }
  }, [open, existingProduct?.id, allProducts.length]); // ← use stable ID, not object reference

  /* Update price for specific tier + subcat */
  const setPrice = (tier, subcatId, value) => {
    setPrices((prev) => ({
      ...prev,
      [tier]: {
        ...(prev[tier] || {}),
        [subcatId]: value === '' ? '' : Number(value),
      },
    }));
  };

  /* Handle image */
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  /* Save — always uses FormData (consistent create + update) */
  const handleSave = async () => {
    if (!name.trim()) return toast.error('Product name required');
    if (!groupId) return toast.error('Please select a group');
    if (subcategories.length === 0) return toast.error('No sub-categories — create one first');

    setIsSaving(true);
    try {
      /* Clean prices — remove empty/invalid values */
      const cleanPrices = { standard: {}, luxury: {} };
      Object.entries(prices?.standard || {}).forEach(([k, v]) => {
        const num = Number(v);
        if (!isNaN(num) && num > 0) cleanPrices.standard[k] = num;
      });
      Object.entries(prices?.luxury || {}).forEach(([k, v]) => {
        const num = Number(v);
        if (!isNaN(num) && num > 0) cleanPrices.luxury[k] = num;
      });

      /* ═══════════════════════════════════════════════════════
         Always send FormData — this is what works for create
         ═══════════════════════════════════════════════════════ */
      const fd = new FormData();
      fd.append('itemId', itemId || '');
      fd.append('name', name.trim());
      fd.append('description', (description || '').trim());
      fd.append('groupId', groupId);
      fd.append('unit', unit || 'sq.ft');
      fd.append('categoryType', categoryType);   /* ← critical */
      fd.append('prices', JSON.stringify(cleanPrices));
      fd.append('notes', notes || '');
      fd.append('isActive', 'true');

      if (imageFile) fd.append('image', imageFile);
      if (!isEditing && user?.id) fd.append('createdBy', user.id);

      /* ─── Debug: log every FormData entry ─── */
      console.log('[InteriorProductForm] Sending FormData:');
      for (const [k, v] of fd.entries()) {
        console.log('  ', k, '=', v instanceof File ? '[File: ' + v.name + ']' : v);
      }

      let record;
      if (isEditing) {
        record = await pb.collection('interior_products').update(existingProduct.id, fd);
      } else {
        record = await pb.collection('interior_products').create(fd);
      }

      /* ─── Debug: log response categoryType ─── */
      console.log('[InteriorProductForm] RESPONSE categoryType:', record?.categoryType);
      console.log('[InteriorProductForm] Full response:', record);

      /* ─── Verify update took effect ─── */
      if (isEditing && record?.categoryType !== categoryType) {
        console.warn('[InteriorProductForm] ⚠️ categoryType mismatch — sent:', categoryType, 'got:', record?.categoryType);
      }

      toast.success(isEditing ? '✅ Product updated' : '✅ Product added');

      /* ─── Optimistic cache update + refetch ─── */
      queryClient.setQueryData(['interior-products'], (old) => {
        if (!Array.isArray(old)) return old;
        return old.map((p) => (p.id === record.id ? { ...p, ...record } : p));
      });

      await queryClient.invalidateQueries({ queryKey: ['interior-products'] });
      await queryClient.refetchQueries({ queryKey: ['interior-products'] });
      queryClient.invalidateQueries({ queryKey: ['interior-products-all-mini'] });

      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      console.error('[InteriorProductForm] Save error:', err);
      let msg = err.message || 'Save failed';
      if (err.originalError?.data?.message) msg = err.originalError.data.message;
      if (err.originalError?.data?.data) {
        const fields = Object.entries(err.originalError.data.data)
          .map(([f, e]) => `${f}: ${e.message}`)
          .join('; ');
        if (fields) msg += ' → ' + fields;
      }
      toast.error('❌ ' + msg);
    } finally {
      setIsSaving(false);
    }
  };

  const activeSubcats = useMemo(
    () => subcategories.filter((s) => s.isActive),
    [subcategories]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-emerald-600" />
            {isEditing ? 'Edit Product' : 'Add New Product'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Item ID + Name */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-emerald-700">Item ID</Label>
              <Input value={itemId} disabled className="bg-slate-50 font-mono" />
            </div>
            <div className="md:col-span-2 space-y-1.5">
              <Label className="text-emerald-700 font-medium">Product Name *</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. King Size Bed"
                className="border-emerald-200"
              />
            </div>
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label className="text-emerald-700">Description</Label>
            <Textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the product…"
              className="border-emerald-200"
            />
          </div>

          {/* Group + Unit + Category Type */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-emerald-700 font-medium">Group (Room) *</Label>
              <Select value={groupId} onValueChange={setGroupId}>
                <SelectTrigger className="border-emerald-200">
                  <SelectValue placeholder="Select group…" />
                </SelectTrigger>
                <SelectContent>
                  {groups.length === 0 ? (
                    <div className="p-2 text-xs text-slate-400">No groups — create one first</div>
                  ) : (
                    groups.map((g) => (
                      <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-emerald-700 font-medium">Unit</Label>
              <Select value={unit} onValueChange={setUnit}>
                <SelectTrigger className="border-emerald-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {UNIT_OPTIONS.map((u) => (
                    <SelectItem key={u} value={u}>{u}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-emerald-700 font-medium">Property Type</Label>
              <Select value={categoryType} onValueChange={setCategoryType}>
                <SelectTrigger className="border-emerald-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_TYPES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* ═══════════════════════════════════════════════
              PRICE MATRIX (2 rows × N subcategories)
              ═══════════════════════════════════════════════ */}
          <div className="border-2 border-emerald-200 rounded-xl overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-3 border-b border-emerald-200">
              <p className="text-sm font-bold text-emerald-800">Price Matrix</p>
              <p className="text-[10px] text-emerald-600 mt-0.5">
                {activeSubcats.length} sub-categories × 2 design styles = {activeSubcats.length * 2} prices
              </p>
            </div>

            {activeSubcats.length === 0 ? (
              <div className="p-6 text-center text-xs text-slate-400">
                No sub-categories found. Create them from the Sub-Categories manager.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left p-2.5 font-semibold text-slate-600 text-xs uppercase tracking-wide w-32">
                        Design Style
                      </th>
                      {activeSubcats.map((s) => (
                        <th key={s.id} className="p-2.5 font-semibold text-slate-600 text-xs uppercase tracking-wide">
                          {s.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Standard row */}
                    <tr className="border-b border-slate-100">
                      <td className="p-2.5 bg-slate-50/50">
                        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-700">
                          <span className="w-2 h-2 rounded-full bg-blue-500" /> Standard
                        </span>
                      </td>
                      {activeSubcats.map((s) => (
                        <td key={s.id} className="p-2">
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">₹</span>
                            <Input
                              type="number"
                              value={prices.standard?.[s.id] ?? ''}
                              onChange={(e) => setPrice('standard', s.id, e.target.value)}
                              placeholder="0"
                              className="h-9 pl-6 text-sm"
                            />
                          </div>
                        </td>
                      ))}
                    </tr>
                    {/* Luxury row */}
                    <tr>
                      <td className="p-2.5 bg-slate-50/50">
                        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-700">
                          <span className="w-2 h-2 rounded-full bg-amber-500" /> Luxury
                        </span>
                      </td>
                      {activeSubcats.map((s) => (
                        <td key={s.id} className="p-2">
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">₹</span>
                            <Input
                              type="number"
                              value={prices.luxury?.[s.id] ?? ''}
                              onChange={(e) => setPrice('luxury', s.id, e.target.value)}
                              placeholder="0"
                              className="h-9 pl-6 text-sm"
                            />
                          </div>
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Image */}
          <div className="space-y-1.5">
            <Label className="text-emerald-700">Product Image (optional)</Label>
            <div className="flex items-center gap-3">
              {imagePreview ? (
                <div className="relative">
                  <img
                    src={imagePreview}
                    alt="preview"
                    className="w-20 h-20 object-cover rounded-lg border-2 border-emerald-300"
                  />
                  <button
                    type="button"
                    onClick={() => { setImageFile(null); setImagePreview(''); }}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <label className="w-20 h-20 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-emerald-400">
                  <ImagePlus className="w-6 h-6 text-slate-400" />
                  <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                </label>
              )}
              <div className="text-xs text-slate-500">JPG / PNG · Max 5MB</div>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-1.5">
            <Label className="text-emerald-700">Internal Notes</Label>
            <Textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="border-emerald-200"
              placeholder="Optional notes…"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
          >
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isEditing ? 'Update Product' : 'Create Product'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InteriorProductForm;
